import {
  ArrowDropDown,
  CheckBox,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import moment from 'moment';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PercentageField } from '~/components/GenericForm/PercentageField';
import { AllEnums, Enum } from '~/data/enums/types';
import { UnitPosition, UnitPositionCustomData } from '~/data/unitPositions';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import { UpCustomDataAttribute } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data/customDataUnitPosition';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { filterActiveEnums } from '~/utils/helper';

import { useSxStyles } from './useStyles';
import { useUnitPositionPercentage } from './useUnitPositionPercentage';

type Props = {
  scheduleFormMethods: UseFormReturn<Form>;
  allEnums: AllEnums;
  unitPositions: UnitPosition[];
  selectedUnitPositionIds: number[];
  index: number;
};
export const UnitPositionPercentage = ({
  unitPositions,
  selectedUnitPositionIds,
  scheduleFormMethods,
  allEnums,
  index,
}: Props) => {
  const { control, getValues } = scheduleFormMethods;
  const {
    upCustomData,
    filteredUnitPositions,
    countTotalPercent,
    totalPercent,
    loading,
    upCustomDataAttribute,
  } = useUnitPositionPercentage(
    unitPositions,
    selectedUnitPositionIds,
    getValues,
    index,
  );

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const PercentField = ({ fup }: { fup: UnitPosition }) => {
    return (
      <>
        {/* @ts-ignore */}
        <Controller
          control={control}
          name={`schedule.${index}.empPosition.${fup.fUnitPositionId}-percent`}
          render={({ field: { onChange, value } }) => (
            <PercentageField
              onChange={(e) => {
                countTotalPercent(value || 0, parseInt(e.target.value));
                onChange(parseInt(e.target.value) || 0);
              }}
              value={value || 0}
              type="text"
              size={'small'}
            />
          )}
        />
      </>
    );
  };

  const enumNameField = (enumValue: Enum[], id: string) => {
    if (id === undefined || id === '') return '';
    const specificEnum = enumValue.find((en) => en.code === id);
    return specificEnum === undefined ? id : specificEnum.name;
  };

  const customField = (
    customDataAttributes: UpCustomDataAttribute,
    upCustomData: UnitPositionCustomData,
  ) => {
    const strName = customDataAttributes.fCustomDataAttributeName;
    const strType = customDataAttributes.fCustomDataAttributeDataType;

    if (!customDataAttributes || !upCustomData.hasOwnProperty(strName)) {
      return '';
    }
    switch (strType) {
      case 'BOOLEAN':
        return upCustomData[strName] === 'true' ? (
          <CheckBox />
        ) : (
          <CheckBoxOutlineBlank />
        );
      case 'TEXT':
      case 'STRING':
        return upCustomData[strName];
      case 'DATE':
        return moment(
          upCustomData[strName],
          scheduledDateFormats.dashFormat,
        ).format(scheduledDateFormats.dotFormat);
      case 'NUMBER':
        return upCustomData[strName]
          ? upCustomData[strName].toString().replace('.', ',')
          : '';
      case 'ENUM':
        return enumNameField(
          filterActiveEnums(
            allEnums[
              customDataAttributes.fCustomDataAttributeDataSubtype as string
            ],
          ),
          upCustomData[strName] as string,
        );
      case 'MULTISELECT':
        const strMultiValues = (upCustomData[strName] as string[])
          .map((up) =>
            enumNameField(
              filterActiveEnums(
                allEnums[
                  customDataAttributes.fCustomDataAttributeDataSubtype as string
                ],
              ),
              up,
            ),
          )
          .join(', ');

        return strMultiValues.slice(0, -2);
      default:
        return '';
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  const showCustomData = upCustomData.length > 0;

  return (
    <Paper>
      <Table size={'small'}>
        <TableBody>
          <TableRow>
            <TableCell
              sx={{ ...sxStyles.tableHeaderRow, ...sxStyles.header }}
              colSpan={showCustomData ? 3 : 2}
            >
              {t('fEmpUnitPositions')}
            </TableCell>
          </TableRow>
          {filteredUnitPositions &&
            filteredUnitPositions.map((fup) => (
              <TableRow key={fup.fUnitPositionId}>
                <TableCell>{fup.fUnitPositionName}</TableCell>
                <TableCell align={'right'} sx={{ width: '120px' }}>
                  {PercentField({ fup })}
                </TableCell>
                {showCustomData && (
                  <TableCell>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ArrowDropDown />}
                        sx={sxStyles.header}
                      >
                        {t('laCustomData')}
                      </AccordionSummary>
                      <AccordionDetails sx={{ padding: 0 }}>
                        <Divider />
                        <Table size={'small'}>
                          <TableBody>
                            {upCustomData.map((upc) => (
                              <TableRow key={upc.code}>
                                <TableCell sx={sxStyles.customDataLabel}>
                                  {upc.name}
                                </TableCell>
                                <TableCell>
                                  {fup.fUnitPositionCustomData.hasOwnProperty(
                                    upc.code,
                                  ) &&
                                    customField(
                                      upCustomDataAttribute[upc.code],
                                      fup.fUnitPositionCustomData,
                                    )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  </TableCell>
                )}
              </TableRow>
            ))}
          <TableRow>
            <TableCell
              sx={{ ...sxStyles.tableHeaderRow, ...sxStyles.totalRow }}
            >
              {t('laTotal')}
            </TableCell>
            <TableCell
              sx={{ ...sxStyles.tableHeaderRow, ...sxStyles.totalRow }}
              align={'right'}
            >{`${totalPercent} %`}</TableCell>
            {showCustomData && (
              <TableCell
                sx={{ ...sxStyles.tableHeaderRow, ...sxStyles.totalRow }}
              ></TableCell>
            )}
          </TableRow>

          {totalPercent > 100 && (
            <TableRow>
              <TableCell
                colSpan={showCustomData ? 3 : 2}
                sx={{ ...sxStyles.tableHeaderRow, ...sxStyles.error }}
              >
                {t('laWarningPercentageOver100')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};
